import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/feature/Toast';
import { useAuth } from '@/context/AuthContext';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { cargarLogoAmzedel, NOMBRE_FARMACIA, SISTEMA_DESCRIPCION } from '@/lib/marca';
import { dibujarMarca } from '@/lib/pdf/encabezado';

type Periodo = 'hoy' | 'semana' | 'mes' | 'año' | 'personalizado';

interface Venta {
  id: string;
  fecha: string;
  total: number;
  metodo_pago: string;
  monto_recibido: number;
  cambio: number;
  nombre_nit: string;
  numero_nit: string;
  usuario_id: string;
}

interface DetalleVenta {
  id: string;
  venta_id: string;
  producto_id: string;
  cantidad: number;
  precio_unitario: number;
  subtotal: number;
}

interface ProductoVendido {
  nombre: string;
  cantidad: number;
  total: number;
}

interface VentaPorPeriodo {
  label: string;
  ventas: number;
  ingresos: number;
}

interface VentaPorMetodo {
  metodo: string;
  cantidad: number;
  total: number;
}

const periodLabels: Record<string, string> = {
  'Enero': 'Ene', 'Febrero': 'Feb', 'Marzo': 'Mar', 'Abril': 'Abr',
  'Mayo': 'May', 'Junio': 'Jun', 'Julio': 'Jul', 'Agosto': 'Ago',
  'Septiembre': 'Sep', 'Octubre': 'Oct', 'Noviembre': 'Nov', 'Diciembre': 'Dic',
};

export default function ReportesPage() {
  const { addToast } = useToast();
  const { user } = useAuth();

  const [ventas, setVentas] = useState<Venta[]>([]);
  const [detalles, setDetalles] = useState<DetalleVenta[]>([]);
  const [productosNombres, setProductosNombres] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  const [periodo, setPeriodo] = useState<Periodo>('mes');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [exporting, setExporting] = useState(false);
  const [inventarioValorizado, setInventarioValorizado] = useState(0);
  const [productosAgotados, setProductosAgotados] = useState(0);
  const [totalProductosInventario, setTotalProductosInventario] = useState(0);

  const getDateRange = useCallback((p: Periodo): { inicio: Date; fin: Date } => {
    const now = new Date();
    const fin = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

    let inicio: Date;
    switch (p) {
      case 'hoy':
        inicio = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        break;
      case 'semana': {
        const day = now.getDay();
        const diff = day === 0 ? 6 : day - 1;
        inicio = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diff, 0, 0, 0);
        break;
      }
      case 'mes':
        inicio = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
        break;
      case 'año':
        inicio = new Date(now.getFullYear(), 0, 1, 0, 0, 0);
        break;
      default:
        inicio = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
    }
    return { inicio, fin };
  }, []);

  useEffect(() => {
    loadData();
    loadInventario();
  }, []);

  const loadInventario = async () => {
    try {
      const { data } = await supabase.from('productos').select('precio_compra, precio, stock');
      if (data) {
        let valorizado = 0;
        let agotados = 0;
        (data as { precio_compra: number; precio: number; stock: number }[]).forEach((p) => {
          const costo = p.precio_compra || p.precio || 0;
          valorizado += costo * p.stock;
          if (p.stock <= 0) agotados += 1;
        });
        setInventarioValorizado(valorizado);
        setProductosAgotados(agotados);
        setTotalProductosInventario(data.length);
      }
    } catch { /* optional */ }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const { data: ventasData, error: ventasError } = await supabase
        .from('ventas')
        .select('*')
        .order('fecha', { ascending: false });

      if (ventasError) throw ventasError;

      if (ventasData && ventasData.length > 0) {
        setVentas(ventasData as Venta[]);

        const ventaIds = ventasData.map((v) => v.id);

        const { data: detData, error: detError } = await supabase
          .from('detalle_venta')
          .select('*')
          .in('venta_id', ventaIds);

        if (!detError && detData) {
          setDetalles(detData as DetalleVenta[]);

          const prodIds = [...new Set(detData.map((d) => d.producto_id))];
          const { data: prodsData } = await supabase
            .from('productos')
            .select('id, nombre')
            .in('id', prodIds);

          if (prodsData) {
            const nameMap: Record<string, string> = {};
            prodsData.forEach((p: { id: string; nombre: string }) => {
              nameMap[p.id] = p.nombre;
            });
            setProductosNombres(nameMap);
          }
        }
      }
    } catch {
      addToast('Error al cargar datos de reportes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fechaInicioReal = useMemo(() => {
    if (periodo === 'personalizado' && fechaInicio) return new Date(fechaInicio);
    return getDateRange(periodo).inicio;
  }, [periodo, fechaInicio, getDateRange]);

  const fechaFinReal = useMemo(() => {
    if (periodo === 'personalizado' && fechaFin) return new Date(fechaFin + 'T23:59:59');
    return getDateRange(periodo).fin;
  }, [periodo, fechaFin, getDateRange]);

  const ventasFiltradas = useMemo(() => {
    return ventas.filter((v) => {
      const d = new Date(v.fecha);
      return d >= fechaInicioReal && d <= fechaFinReal;
    });
  }, [ventas, fechaInicioReal, fechaFinReal]);

  const detallesFiltrados = useMemo(() => {
    const ids = new Set(ventasFiltradas.map((v) => v.id));
    return detalles.filter((d) => ids.has(d.venta_id));
  }, [detalles, ventasFiltradas]);

  const stats = useMemo(() => {
    const totalVentas = ventasFiltradas.reduce((s, v) => s + Number(v.total), 0);
    const cantidad = ventasFiltradas.length;
    const totalProductos = detallesFiltrados.reduce((s, d) => s + d.cantidad, 0);

    const todasVentas = ventas.reduce((s, v) => s + Number(v.total), 0);

    return { totalVentas, cantidad, totalProductos, totalHistorico: todasVentas };
  }, [ventasFiltradas, detallesFiltrados, ventas]);

  const productosMasVendidos: ProductoVendido[] = useMemo(() => {
    const map: Record<string, { nombre: string; cantidad: number; total: number }> = {};
    detallesFiltrados.forEach((d) => {
      const nombre = productosNombres[d.producto_id] || 'Desconocido';
      if (!map[d.producto_id]) {
        map[d.producto_id] = { nombre, cantidad: 0, total: 0 };
      }
      map[d.producto_id].cantidad += d.cantidad;
      map[d.producto_id].total += Number(d.subtotal);
    });
    return Object.values(map)
      .sort((a, b) => b.cantidad - a.cantidad)
      .slice(0, 10);
  }, [detallesFiltrados, productosNombres]);

  const ventasPorPeriodo: VentaPorPeriodo[] = useMemo(() => {
    if (periodo === 'hoy' || periodo === 'semana') {
      const days: Record<string, { ventas: number; ingresos: number }> = {};
      const options: Intl.DateTimeFormatOptions = { weekday: 'short' };

      for (let d = new Date(fechaInicioReal); d <= fechaFinReal; d.setDate(d.getDate() + 1)) {
        const label = d.toLocaleDateString('es-ES', options);
        days[label] = { ventas: 0, ingresos: 0 };
      }

      ventasFiltradas.forEach((v) => {
        const label = new Date(v.fecha).toLocaleDateString('es-ES', options);
        if (days[label]) {
          days[label].ventas += 1;
          days[label].ingresos += Number(v.total);
        }
      });

      return Object.entries(days).map(([label, val]) => ({
        label: label.charAt(0).toUpperCase() + label.slice(1),
        ventas: val.ventas,
        ingresos: val.ingresos,
      }));
    }

    if (periodo === 'mes' || periodo === 'personalizado') {
      const weeks: Record<string, { ventas: number; ingresos: number }> = {};
      ventasFiltradas.forEach((v) => {
        const d = new Date(v.fecha);
        const weekStart = new Date(d);
        weekStart.setDate(d.getDate() - d.getDay() + 1);
        const label = `Sem ${weekStart.getDate()}/${weekStart.getMonth() + 1}`;
        if (!weeks[label]) weeks[label] = { ventas: 0, ingresos: 0 };
        weeks[label].ventas += 1;
        weeks[label].ingresos += Number(v.total);
      });
      return Object.entries(weeks).map(([label, val]) => ({ label, ...val }));
    }

    const months: Record<string, { ventas: number; ingresos: number }> = {};
    const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    meses.forEach((m) => { months[m] = { ventas: 0, ingresos: 0 }; });

    ventasFiltradas.forEach((v) => {
      const m = new Date(v.fecha).getMonth();
      months[meses[m]].ventas += 1;
      months[meses[m]].ingresos += Number(v.total);
    });

    return meses.map((m) => ({
      label: periodLabels[m] || m,
      ventas: months[m].ventas,
      ingresos: months[m].ingresos,
    }));
  }, [ventasFiltradas, periodo, fechaInicioReal, fechaFinReal]);

  const ventasPorMetodo: VentaPorMetodo[] = useMemo(() => {
    const map: Record<string, { cantidad: number; total: number }> = {};
    ventasFiltradas.forEach((v) => {
      if (!map[v.metodo_pago]) map[v.metodo_pago] = { cantidad: 0, total: 0 };
      map[v.metodo_pago].cantidad += 1;
      map[v.metodo_pago].total += Number(v.total);
    });
    return Object.entries(map).map(([metodo, val]) => ({ metodo, ...val }));
  }, [ventasFiltradas]);

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const today = new Date().toLocaleDateString('es-ES');

      // Encabezado oscuro
      doc.setFillColor(31, 46, 44);
      doc.rect(0, 0, pageWidth, 38, 'F');

      const logo = await cargarLogoAmzedel();
      if (logo) {
        const size = 18;
        const ratio = logo.width / logo.height || 1;
        let w = size;
        let h = size;
        if (ratio > 1) h = size / ratio;
        else w = size * ratio;
        try {
          doc.addImage(logo.dataUrl, logo.format, 14, 9, w, h);
        } catch {
          dibujarMarca(doc, null, 14, 9, size);
        }
      } else {
        dibujarMarca(doc, null, 14, 9, 18);
      }

      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.text(NOMBRE_FARMACIA, 38, 18);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(198, 210, 205);
      doc.text(SISTEMA_DESCRIPCION, 38, 25);

      doc.setFontSize(9);
      doc.setTextColor(180, 180, 180);
      doc.text(`Fecha: ${today}`, pageWidth - 14, 18, { align: 'right' });
      doc.text(`Generado por: ${user?.nombre || 'Sistema'}`, pageWidth - 14, 26, { align: 'right' });

      // Título
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(31, 46, 44);
      doc.setFontSize(18);
      doc.text('Reporte Financiero', 14, 52);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Periodo: ${fechaInicioReal.toLocaleDateString('es-ES')} — ${fechaFinReal.toLocaleDateString('es-ES')}`, 14, 60);
      doc.text(`Total ventas: ${stats.cantidad} | Ingresos: Bs. ${stats.totalVentas.toFixed(2)}`, 14, 67);

      // Tabla de ventas
      const tableData = ventasFiltradas.slice(0, 30).map((v) => [
        new Date(v.fecha).toLocaleDateString('es-ES'),
        new Date(v.fecha).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
        `Bs. ${Number(v.total).toFixed(2)}`,
        v.metodo_pago,
        v.nombre_nit || 'Consumidor Final',
        v.numero_nit || '—',
      ]);

      (doc as any).autoTable({
        startY: 74,
        head: [['Fecha', 'Hora', 'Total', 'Metodo', 'Cliente', 'NIT/CI']],
        body: tableData,
        headStyles: { fillColor: [31, 46, 44], textColor: [255, 255, 255], fontSize: 9 },
        bodyStyles: { fontSize: 8, textColor: [50, 50, 50] },
        alternateRowStyles: { fillColor: [245, 248, 247] },
        styles: { cellPadding: 3 },
      });

      const finalY1 = (doc as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;

      // Resumen por metodo de pago
      const metodoY = finalY1 + 12;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(31, 46, 44);
      doc.text('Resumen por Metodo de Pago', 14, metodoY);

      const metodoData = ventasPorMetodo.map((m) => [
        m.metodo,
        m.cantidad.toString(),
        `Bs. ${m.total.toFixed(2)}`,
      ]);

      (doc as any).autoTable({
        startY: metodoY + 8,
        head: [['Metodo', 'Cantidad', 'Total']],
        body: metodoData,
        headStyles: { fillColor: [99, 198, 167], textColor: [255, 255, 255], fontSize: 9 },
        bodyStyles: { fontSize: 9 },
        styles: { cellPadding: 3 },
      });

      const finalY2 = (doc as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;

      // Pie de pagina
      const footerY = finalY2 + 14;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(130, 130, 130);
      doc.text(`Este reporte fue generado automaticamente por el sistema ${NOMBRE_FARMACIA}.`, pageWidth / 2, footerY, { align: 'center' });
      doc.text('Firma digital del sistema — Documento valido para control interno.', pageWidth / 2, footerY + 6, { align: 'center' });

      doc.save(`Reporte_Financiero_${today.replace(/\//g, '-')}.pdf`);
      addToast('PDF exportado correctamente', 'success');
    } catch (err) {
      console.error('Error al exportar PDF:', err);
      addToast('Error al exportar PDF', 'error');
    } finally {
      setExporting(false);
    }
  };

  const formatBs = (val: number) => `Bs. ${val.toFixed(2)}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
          <p className="text-foreground-500 text-sm">Cargando reportes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-800">Reporte Financiero</h1>
          <p className="text-foreground-500 text-sm mt-1">Análisis de ventas y rendimiento</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportPDF}
            disabled={exporting || ventasFiltradas.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-background-300 disabled:text-foreground-400 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap text-sm"
          >
            {exporting ? (
              <><i className="ri-loader-4-line animate-spin"></i><span>Exportando...</span></>
            ) : (
              <><i className="ri-file-pdf-line"></i><span>Exportar PDF</span></>
            )}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-background-200 p-4">
        <div className="flex flex-wrap items-center gap-2">
          {(['hoy', 'semana', 'mes', 'año', 'personalizado'] as Periodo[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriodo(p)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                periodo === p
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {p === 'hoy' ? 'Hoy' : p === 'semana' ? 'Esta Semana' : p === 'mes' ? 'Este Mes' : p === 'año' ? 'Este Año' : 'Personalizado'}
            </button>
          ))}
        </div>

        {periodo === 'personalizado' && (
          <div className="flex flex-col sm:flex-row gap-3 mt-3">
            <div className="flex items-center gap-2">
              <label className="text-xs text-foreground-500 whitespace-nowrap">Desde:</label>
              <input
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                className="px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-foreground-700 text-sm focus:outline-none focus:border-primary-400"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-foreground-500 whitespace-nowrap">Hasta:</label>
              <input
                type="date"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
                className="px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-foreground-700 text-sm focus:outline-none focus:border-primary-400"
              />
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon="ri-money-dollar-circle-line"
          iconBg="bg-primary-100"
          iconColor="text-primary-600"
          label="Total de Ingresos"
          value={formatBs(stats.totalVentas)}
          sub={stats.totalHistorico > 0 ? `Total historico: ${formatBs(stats.totalHistorico)}` : undefined}
        />
        <StatCard
          icon="ri-shopping-bag-line"
          iconBg="bg-accent-100"
          iconColor="text-accent-600"
          label="Ventas Realizadas"
          value={stats.cantidad.toString()}
        />
        <StatCard
          icon="ri-archive-line"
          iconBg="bg-amber-100"
          iconColor="text-amber-600"
          label="Inventario Valorizado"
          value={formatBs(inventarioValorizado)}
          sub={`${totalProductosInventario} productos en stock`}
        />
        <StatCard
          icon="ri-close-circle-line"
          iconBg="bg-accent-100"
          iconColor="text-accent-600"
          label="Productos Agotados"
          value={productosAgotados.toString()}
        />
      </div>

      {ventasFiltradas.length === 0 ? (
        <div className="bg-white rounded-xl border border-background-200 p-16 text-center">
          <div className="w-20 h-20 bg-background-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <i className="ri-bar-chart-2-line text-4xl text-foreground-300"></i>
          </div>
          <h3 className="text-lg font-semibold text-foreground-600 mb-1">Sin datos disponibles</h3>
          <p className="text-foreground-400 text-sm max-w-md mx-auto">
            No se encontraron ventas en el período seleccionado. Realiza algunas ventas desde el Punto de Venta y vuelve aquí.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-background-200 p-5">
              <h3 className="font-heading text-base font-semibold text-foreground-800 mb-4">
                {periodo === 'año' ? 'Ventas Mensuales' : 'Ventas por Período'}
              </h3>
              {ventasPorPeriodo.length > 0 ? (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {ventasPorPeriodo.map((p, i) => (
                    <div
                      key={p.label}
                      className="flex items-center justify-between p-3 rounded-lg bg-background-50 hover:bg-background-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-xs font-semibold">
                          {i + 1}
                        </span>
                        <span className="text-sm font-medium text-foreground-700">{p.label}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-xs text-foreground-500">{p.ventas} ventas</span>
                        <span className="text-sm font-semibold text-foreground-700">{formatBs(p.ingresos)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-foreground-400 text-sm">Sin datos</div>
              )}
            </div>

            <div className="bg-white rounded-xl border border-background-200 p-5">
              <h3 className="font-heading text-base font-semibold text-foreground-800 mb-4">Productos Más Vendidos</h3>
              {productosMasVendidos.length > 0 ? (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {productosMasVendidos.map((p, i) => (
                    <div
                      key={p.nombre}
                      className="flex items-center justify-between p-3 rounded-lg bg-background-50 hover:bg-background-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-xs font-semibold">
                          {i + 1}
                        </span>
                        <span className="text-sm font-medium text-foreground-700">{p.nombre}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-xs text-foreground-500">{p.cantidad} unid.</span>
                        <span className="text-sm font-semibold text-foreground-700">{formatBs(p.total)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-foreground-400 text-sm">Sin datos</div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-background-200 p-5">
            <h3 className="font-heading text-base font-semibold text-foreground-800 mb-4">Ventas por Método de Pago</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {ventasPorMetodo.length > 0 ? (
                ventasPorMetodo.map((m) => (
                  <div key={m.metodo} className="flex items-center gap-4 p-4 rounded-xl bg-background-50 border border-background-200">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      m.metodo === 'Efectivo' ? 'bg-primary-100 text-primary-600' : 'bg-accent-100 text-accent-600'
                    }`}>
                      <i className={m.metodo === 'Efectivo' ? 'ri-money-dollar-circle-line text-xl' : 'ri-qr-code-line text-xl'}></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-700">{m.metodo}</p>
                      <p className="text-xs text-foreground-500">{m.cantidad} ventas — {formatBs(m.total)}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-foreground-400 text-sm col-span-full text-center py-8">Sin datos</p>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-background-200 p-5">
            <h3 className="font-heading text-base font-semibold text-foreground-800 mb-4">Últimas Ventas</h3>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {ventasFiltradas.slice(0, 10).map((v) => (
                <div
                  key={v.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-background-50 hover:bg-background-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                      v.metodo_pago === 'Efectivo' ? 'bg-primary-100 text-primary-600' : 'bg-accent-100 text-accent-600'
                    }`}>
                      <i className={v.metodo_pago === 'Efectivo' ? 'ri-money-dollar-circle-line' : 'ri-qr-code-line'}></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-700">
                        {new Date(v.fecha).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </p>
                      <p className="text-xs text-foreground-400">{v.metodo_pago}{v.nombre_nit ? ` — ${v.nombre_nit}` : ''}</p>
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-foreground-700">{formatBs(Number(v.total))}</span>
                </div>
              ))}
              {ventasFiltradas.length === 0 && (
                <p className="text-foreground-400 text-sm text-center py-8">Sin ventas en este período</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ icon, iconBg, iconColor, label, value, sub }: {
  icon: string;
  iconBg: string;
  iconColor: string;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-background-200 p-5 hover:border-background-300 transition-all duration-200">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconBg} ${iconColor}`}>
          <i className={`${icon} text-xl`}></i>
        </div>
        <div>
          <p className="text-xs text-foreground-500">{label}</p>
          <p className="text-xl font-bold text-foreground-800">{value}</p>
        </div>
      </div>
      {sub && <p className="text-xs text-foreground-400">{sub}</p>}
    </div>
  );
}